import { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { communityApi } from '@/api/community';
import { messagingApi } from '@/api/messaging';
import { callLogApi, neighborsApi, type Neighbor } from '@/api/neighbors';
import type { ConversationSummary, MessageSummary } from '@/api/types';
import { homeKeys } from '@/features/home/queries';
import { useSessionStore } from '@/state/sessionStore';

/**
 * A message in the cache. Messages this tab is still sending carry a
 * `localStatus` and a temporary id until the server acknowledges them.
 */
export type ChatMessage = MessageSummary & { localStatus?: 'sending' | 'failed' };

/** Query keys (data-model.md B2). */
export const messagingKeys = {
  allConversations: ['conversations'] as const,
  conversations: (archived = false) => ['conversations', { archived }] as const,
  messages: (conversationId: string) => ['messages', conversationId] as const,
  communityGroup: ['groupChat'] as const,
  neighbors: ['neighbors'] as const,
  callLog: ['callLog'] as const,
  /** Who I have a chat with is online right now. */
  presence: ['presence'] as const,
};

function useSignedIn() {
  return useSessionStore((s) => s.status === 'signedIn');
}

/** The members I have a chat with who are online now (kept up to date by `presence:changed`). */
export function usePresence(): Set<string> {
  const signedIn = useSignedIn();
  const { data } = useQuery({
    queryKey: messagingKeys.presence,
    queryFn: messagingApi.presence,
    enabled: signedIn,
    refetchOnWindowFocus: true,
  });
  return useMemo(() => new Set(data?.online ?? []), [data]);
}

/** The active conversation list (GET /conversations), including each one's unread count. */
export function useConversations() {
  const signedIn = useSignedIn();
  return useQuery({
    queryKey: messagingKeys.conversations(false),
    queryFn: messagingApi.listConversations,
    enabled: signedIn,
  });
}

export function useArchivedConversations(enabled = true) {
  return useQuery({
    queryKey: messagingKeys.conversations(true),
    queryFn: messagingApi.listArchived,
    enabled,
  });
}

/** Unread messages across conversations: the Messages badge and the tab title (FR-031, WEB-007). */
export function useUnreadMessagesCount(): number {
  const { data } = useConversations();
  return (data ?? []).reduce(
    (total, conversation) =>
      total + (conversation.type === 'group' ? 0 : (conversation.unreadCount ?? 0)),
    0,
  );
}

/** One conversation, from the active list or, failing that, the archived one. */
export function useConversation(conversationId: string) {
  const active = useConversations();
  const inActive = active.data?.find((c) => c.id === conversationId);
  const archived = useArchivedConversations(Boolean(active.data) && !inActive);
  const found = inActive ?? archived.data?.find((c) => c.id === conversationId);
  // A conversation opened just now (Offer to help, a QR code, a new chat) is newer than the
  // cached list: fetch the list again, once per conversation.
  const refetchedFor = useRef<string | null>(null);
  const { refetch } = active;
  const missing = Boolean(active.data && archived.data) && !found && !active.isFetching;
  useEffect(() => {
    if (missing && refetchedFor.current !== conversationId) {
      refetchedFor.current = conversationId;
      void refetch();
    }
  }, [conversationId, missing, refetch]);
  return found;
}

export function useMessages(conversationId: string | undefined) {
  return useQuery<ChatMessage[]>({
    queryKey: messagingKeys.messages(conversationId ?? ''),
    queryFn: () => messagingApi.listMessages(conversationId ?? ''),
    enabled: Boolean(conversationId),
  });
}

export function useCommunityGroup() {
  return useQuery({
    queryKey: messagingKeys.communityGroup,
    queryFn: messagingApi.getCommunityGroup,
    staleTime: Infinity,
  });
}

export function useNeighbors() {
  return useQuery({ queryKey: messagingKeys.neighbors, queryFn: neighborsApi.list });
}

/** Calls with `contactId` that nobody answered, shown in the conversation (useMissedCalls on Android). */
export function useMissedCalls(contactId: string | undefined) {
  const { data } = useQuery({
    queryKey: messagingKeys.callLog,
    queryFn: callLogApi.list,
    enabled: Boolean(contactId),
  });
  return (data ?? []).filter(
    (call) =>
      call.status === 'ended' &&
      call.outcome === 'missed' &&
      (call.counterpart?.id === contactId ||
        call.callerId === contactId ||
        call.calleeId === contactId),
  );
}

export interface ContactProfile {
  name?: string;
  photoUrl?: string;
  neighbor?: Neighbor;
}

/**
 * Names and photos for the people in conversations: matched neighbours first,
 * then each remaining member's public profile. Never an id as a name
 * (mobile/src/screens/messaging/contactName.ts).
 */
export function useContactProfiles(memberIds: (string | undefined)[]): Map<string, ContactProfile> {
  const neighbors = useNeighbors();
  const byId = new Map((neighbors.data ?? []).map((n) => [n.id, n]));
  const ids = [...new Set(memberIds.filter((id): id is string => Boolean(id)))];
  const missing = neighbors.isPending ? [] : ids.filter((id) => !byId.get(id)?.name);
  const profiles = useQueries({
    queries: missing.map((id) => ({
      queryKey: homeKeys.profile(id),
      queryFn: () => communityApi.publicProfile(id),
      staleTime: 5 * 60_000,
    })),
  });
  const result = new Map<string, ContactProfile>();
  for (const id of ids) {
    const neighbor = byId.get(id);
    const profile = profiles[missing.indexOf(id)]?.data;
    result.set(id, {
      name: neighbor?.name || profile?.member.name || undefined,
      photoUrl: neighbor?.photoUrl ?? profile?.member.photoUrl,
      neighbor,
    });
  }
  return result;
}

/** The other person in a direct conversation. */
export function counterpartId(
  conversation: Pick<ConversationSummary, 'participantIds'> | undefined,
  myId: string | undefined,
): string | undefined {
  if (!conversation) return undefined;
  return conversation.participantIds.find((id) => id !== myId) ?? conversation.participantIds[0];
}

export function useArchiveConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => messagingApi.archive(conversationId),
    onSuccess: (_result, conversationId) => {
      queryClient.setQueryData<ConversationSummary[]>(messagingKeys.conversations(false), (list) =>
        list?.filter((c) => c.id !== conversationId),
      );
      void queryClient.invalidateQueries({ queryKey: messagingKeys.allConversations });
    },
  });
}
